import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete'

const PlacesAutocompleteSafe = PlacesAutocomplete as unknown as React.ComponentType<Record<string, unknown>>

import {
  Box,
  Divider,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spinner,
  Text,
} from '@chakra-ui/react'
import { AroundMeIcon, LocationMarker } from '../icons/icons'

import { LocationMarkerFilled } from '../../components/icons/icons'
import useMobile from '../../hooks/useMobile'
import React, { useEffect, useState, useCallback } from 'react'

const LocationSearch = ({
  location,
  setLocation,
  setCoordinates,
  placeholder,
  fontSize,
  playPage,
}: {
  location: string
  setLocation: (v: string) => void
  setCoordinates?: (v: { lat: number; lng: number }) => void
  placeholder?: string
  fontSize?: string
  playPage?: boolean
}) => {
  // const [ place, setPlace ] = useState<any>(null)

  //const [ lat, setLat ] = useState<number | null>(null)
  //const [ lng, setLng ] = useState<number | null>(null)
  const isMobile = useMobile(700)

  const [locationValue, setLocationValue] = useState(location)
  const [debouncedSearchValue, setDebouncedSearchValue] = useState(location)

  // Sync with parent location prop
  useEffect(() => {
    if (location !== locationValue && location !== debouncedSearchValue) {
      setLocationValue(location)
      setDebouncedSearchValue(location)
    }
  }, [location])

  const handleSelect = async (value: any) => {
    try {
      const results = await geocodeByAddress(value)
      const latLng = await getLatLng(results[0])

      if (setCoordinates) {
        setCoordinates({ lat: latLng.lat, lng: latLng.lng })
      }
      setLocation(value)
      setLocationValue(value)
      setDebouncedSearchValue(value)
    } catch (error) {
      console.log('Geocode error:', error)
    }
  }

  // Debounce the search value to delay PlacesAutocomplete API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(locationValue)
    }, 300)

    return () => {
      clearTimeout(timer)
    }
  }, [locationValue])

  // Sync debounced search value with parent location state
  useEffect(() => {
    if (debouncedSearchValue !== location) {
      setLocation(debouncedSearchValue)
    }
  }, [debouncedSearchValue, setLocation, location])

  // Handler for input changes - immediate update for display, debounced for search
  const handleChange = useCallback((value: string) => {
    setLocationValue(value) // Immediate update for input display
  }, [])

  return (
    <>
      {!playPage && (
        <PlacesAutocompleteSafe value={debouncedSearchValue} onChange={handleChange} onSelect={handleSelect} onError={() => {}}>
          {({ getInputProps, suggestions, getSuggestionItemProps, loading }: any) => (
            <div>
              <Box position="relative">
                <Flex p="12px" gap="10px" alignItems="center">
                  <Input
                    {...getInputProps()}
                    value={locationValue}
                    placeholder={placeholder ?? 'Add a location...'}
                    variant="unstyled"
                    _placeholder={{ color: '#171C22', opacity: '40%' }}
                    color="#171C22"
                    fontWeight="500"
                    fontSize={fontSize ?? 'md'}
                  />
                  {loading ? (
                    <Spinner />
                  ) : (
                    <Icon w="24px" h="24px" color="#171C22">
                      <LocationMarkerFilled />
                    </Icon>
                  )}
                </Flex>

                {locationValue.length > 1 && suggestions.length > 0 && !loading && (
                  <Box
                    w="100%"
                    pos="absolute"
                    zIndex="1"
                    bottom="60px"
                    overflow="hidden"
                    pb="4px"
                    pt="4px"
                    bg="white"
                    boxShadow="0px 6px 35px 5px #0000001F"
                    borderColor="neutral.100"
                    borderWidth="1px"
                    borderRadius="8px"
                  >
                    {/* {(suggestions.length === 0 && ) &&
                <Flex h='42px' p='2px 16px' alignItems='center'>
                    <Text
                        w='100%'
                        textAlign={'center'}
                        textStyle='b-sm'
                        color='neutral.500'
                    >No results found</Text>
                </Flex>
            } */}
                    {suggestions.slice(0, isMobile ? 3 : suggestions.length).map((suggestion: any, i: number) => {
                      return (
                        <>
                          <Flex
                            key={i}
                            h="60px"
                            p="2px 16px"
                            cursor="pointer"
                            alignItems="center"
                            _hover={{ bg: 'neutral.50' }}
                            transition="0.2s ease-out"
                            {...getSuggestionItemProps(suggestion)}
                          >
                            <Box>
                              <Text fontSize="md" fontWeight="600" color="black" noOfLines={1}>
                                {suggestion.formattedSuggestion.mainText}
                              </Text>
                              <Text fontSize="sm" fontWeight="400" color="gray.700" noOfLines={1}>
                                {suggestion.formattedSuggestion.secondaryText}
                              </Text>
                            </Box>
                          </Flex>
                          {i !== suggestions.length - 1 && <Divider />}
                        </>
                      )
                    })}
                  </Box>
                )}
              </Box>
            </div>
          )}
        </PlacesAutocompleteSafe>
      )}
      {playPage && (
        <PlacesAutocompleteSafe value={debouncedSearchValue} onChange={handleChange} onSelect={handleSelect} onError={() => {}}>
          {({ getInputProps, suggestions, getSuggestionItemProps, loading }: any) => (
            <Box position="relative">
              <InputGroup>
                <InputLeftElement>
                  <Icon w="22px" h="22px">
                    <LocationMarker color="black" />
                  </Icon>
                </InputLeftElement>
                <Input
                  {...getInputProps()}
                  value={locationValue}
                  placeholder={placeholder ?? 'Add a location...'}
                  _focus={{
                    _placeholder: { color: 'grey' },
                    outline: 'none',
                    border: 'none',
                    backgroundColor: '#eceef2',
                  }}
                  _placeholder={{ color: 'black', fontSize: fontSize ?? 'md' }}
                  w="100%"
                  color="#171C22"
                  fontWeight="500"
                  fontSize={fontSize ?? 'md'}
                  px="0px"
                  textOverflow={'ellipsis'}
                  border="none"
                  variant="filled"
                />
                <InputRightElement>
                  <Icon
                    w="34px"
                    h="34px"
                    viewBox="0 0 34 34"
                    cursor="pointer"
                    onClick={() => {
                      setLocation('')
                      setLocationValue('')
                      setDebouncedSearchValue('')
                      setCoordinates && setCoordinates({ lat: 0, lng: 0 })
                    }}
                  >
                    {locationValue.length > 0 ? <AroundMeIcon color="none" /> : <AroundMeIcon color="black" />}
                    {/*search.length > 0 ? <Messages color="black"/> : <MessagesFilled/>*/}
                  </Icon>
                </InputRightElement>
              </InputGroup>
              {/* {loading ? <Spinner/> : <></>} */}

              {location.length > 1 && suggestions.length > 0 && !loading && (
                <Box
                  w="250px"
                  zIndex={1000}
                  top="100%"
                  overflow="hidden"
                  pb="4px"
                  pt="4px"
                  bg="white"
                  boxShadow="0px 6px 35px 5px #0000001F"
                  position="absolute"
                  borderColor="neutral.100"
                  borderWidth="1px"
                  borderRadius="8px"
                >
                  {suggestions.map((suggestion: any, i: number) => {
                    return (
                      <>
                        <Flex
                          key={i}
                          h="60px"
                          p="2px 16px"
                          cursor="pointer"
                          alignItems="center"
                          _hover={{ bg: 'neutral.50' }}
                          transition="0.2s ease-out"
                          {...getSuggestionItemProps(suggestion)}
                        >
                          <Box>
                            <Text fontSize="md" fontWeight="600" color="black" noOfLines={1}>
                              {suggestion.formattedSuggestion.mainText}
                            </Text>
                            <Text fontSize="sm" fontWeight="400" color="gray.700" noOfLines={1}>
                              {suggestion.formattedSuggestion.secondaryText}
                            </Text>
                          </Box>
                        </Flex>
                        {i !== suggestions.length - 1 && <Divider />}
                      </>
                    )
                  })}
                </Box>
              )}
            </Box>
          )}
        </PlacesAutocompleteSafe>
      )}
    </>
  )
}

export default LocationSearch
